import React, { useState, useEffect } from 'react';
import { Submission, ScavengerItem } from '../types';
import { huntmasterService, scavengerService, imageService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Rules from './Rules';
import './HuntmasterDashboard.css';

interface Team {
  groupId: string;
  name: string;
  passcode: string;
  teamPicture?: string;
}

const HuntmasterDashboard: React.FC = () => {
  const { logout } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [items, setItems] = useState<ScavengerItem[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'correct' | 'wrong' | 'unknown'>('all');
  const [selectedImage, setSelectedImage] = useState<Submission | null>(null);
  const [submissionsEnabled, setSubmissionsEnabled] = useState(true);
  const [updatingSubmissionStatus, setUpdatingSubmissionStatus] = useState(false);
  const [scoresReleased, setScoresReleased] = useState(false);
  const [updatingScoreRelease, setUpdatingScoreRelease] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [submissionsData, itemsData, teamsRawData, submissionStatusData, scoreReleaseStatusData] = await Promise.all([
        huntmasterService.getAllSubmissions(),
        scavengerService.getItems(),
        huntmasterService.getAllTeams(),
        huntmasterService.getSubmissionStatus(),
        huntmasterService.getScoreReleaseStatus()
      ]);
      
      // Transform teams data to match our interface
      const teamsData = teamsRawData.map((team: any) => ({
        groupId: String(team.id), // Convert id to groupId as string
        name: team.name,
        passcode: team.passcode,
        teamPicture: team.team_picture // Note: backend uses team_picture, frontend uses teamPicture
      }));
      
      console.log('Transformed teams data:', teamsData);
      console.log('Sample submissions data:', submissionsData.slice(0, 3));
      
      setSubmissions(submissionsData);
      setItems(itemsData);
      setTeams(teamsData);
      setSubmissionsEnabled(submissionStatusData.enabled);
      setScoresReleased(scoreReleaseStatusData.released);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubmissions = async () => {
    try {
      setUpdatingSubmissionStatus(true);
      await huntmasterService.toggleSubmissions(!submissionsEnabled);
      setSubmissionsEnabled(!submissionsEnabled);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to toggle submissions');
    } finally {
      setUpdatingSubmissionStatus(false);
    }
  };

  const handleToggleScoreRelease = async () => {
    try {
      setUpdatingScoreRelease(true);
      await huntmasterService.toggleScoreRelease(!scoresReleased);
      setScoresReleased(!scoresReleased);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to toggle score release');
    } finally {
      setUpdatingScoreRelease(false);
    }
  };

  const handleStatusUpdate = async (submissionId: number, status: 'correct' | 'wrong' | 'unknown') => {
    try {
      console.log('Updating submission:', submissionId, 'to status:', status);
      
      if (!submissionId) {
        throw new Error('Submission ID is required');
      }
      
      await huntmasterService.updateSubmissionStatus(submissionId, status);
      console.log('Status update successful');
      await loadData(); // Reload data to reflect changes
    } catch (error: any) {
      console.error('Status update error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to update status');
    }
  };

  const handleViewTeamPage = (teamId: string) => {
    console.log('Looking for team with ID:', teamId);
    console.log('Available teams:', teams);
    
    // Find the team data - try both groupId and converted string comparison
    const team = teams.find(t => t.groupId === teamId || t.groupId === String(teamId) || String(t.groupId) === teamId);
    if (!team) {
      console.error('Team not found. TeamId:', teamId, 'Available team IDs:', teams.map(t => t.groupId));
      setError(`Team ${teamId} not found`);
      return;
    }
    
    console.log('Opening team page for:', team);
    
    // Store team info for the new tab to auto-login
    const teamLoginInfo = { 
      teamId: team.groupId,
      passcode: team.passcode, 
      timestamp: Date.now() 
    };
    
    console.log('Storing team login info:', teamLoginInfo);
    
    // Use a temporary storage key that the new tab can read
    localStorage.setItem('huntmaster_team_redirect', JSON.stringify(teamLoginInfo));
    
    // Open a new tab with the current URL
    const newTab = window.open(window.location.origin, '_blank');
    
    if (newTab) {
      // Focus the new tab
      newTab.focus();
      
      // Clean up the redirect info after a longer delay to ensure the new tab has time to read it
      setTimeout(() => {
        const remainingInfo = localStorage.getItem('huntmaster_team_redirect');
        if (remainingInfo) {
          console.log('Cleaning up redirect info after timeout');
          localStorage.removeItem('huntmaster_team_redirect');
        }
      }, 10000); // Increased to 10 seconds
    } else {
      console.error('Failed to open new tab');
      // Clean up if tab failed to open
      localStorage.removeItem('huntmaster_team_redirect');
    }
  };

  const getItemById = (itemId: number) => {
    return items.find(item => item.id === itemId);
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filter === 'all') return true;
    return submission.status === filter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'correct': return '#28a745';
      case 'wrong': return '#dc3545';
      case 'unknown': return '#ffc107';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="huntmaster-dashboard">
        <div className="loading">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="huntmaster-dashboard">
      <div className="huntmaster-header">
        <div className="header-top">
          <h1>🏆 Huntmaster Dashboard</h1>
          <div className="header-controls">
            <div className="submission-toggle-container">
              <div className="toggle-group">
                <label className="submission-toggle-label">
                  <span className="toggle-text">Submissions</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={submissionsEnabled}
                      onChange={handleToggleSubmissions}
                      disabled={updatingSubmissionStatus}
                      className="toggle-input"
                    />
                    <div className="toggle-slider">
                      <div className="toggle-handle"></div>
                    </div>
                  </div>
                </label>
                <span className={`toggle-status ${submissionsEnabled ? 'enabled' : 'disabled'}`}>
                  {updatingSubmissionStatus 
                    ? 'Updating...' 
                    : submissionsEnabled 
                      ? 'Enabled' 
                      : 'Disabled'
                  }
                </span>
              </div>
            </div>
            <div className="score-toggle-container">
              <div className="toggle-group">
                <label className="score-toggle-label">
                  <span className="toggle-text">Scores</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={scoresReleased}
                      onChange={handleToggleScoreRelease}
                      disabled={updatingScoreRelease}
                      className="toggle-input"
                    />
                    <div className="toggle-slider">
                      <div className="toggle-handle"></div>
                    </div>
                  </div>
                </label>
                <span className={`toggle-status ${scoresReleased ? 'enabled' : 'disabled'}`}>
                  {updatingScoreRelease 
                    ? 'Updating...' 
                    : scoresReleased 
                      ? 'Released' 
                      : 'Hidden'
                  }
                </span>
              </div>
            </div>
            <button className="logout-button" onClick={logout}>
              🚪 Logout
            </button>
          </div>
        </div>
        <div className="stats-summary">
          <div className="stat">
            <span className="stat-number">{submissions.length}</span>
            <span className="stat-label">Total Submissions</span>
          </div>
          <div className="stat">
            <span className="stat-number">{submissions.filter(s => s.status === 'correct').length}</span>
            <span className="stat-label">Approved</span>
          </div>
          <div className="stat">
            <span className="stat-number">{submissions.filter(s => s.status === 'wrong').length}</span>
            <span className="stat-label">Rejected</span>
          </div>
          <div className="stat">
            <span className="stat-number">{submissions.filter(s => s.status === 'unknown').length}</span>
            <span className="stat-label">Pending Review</span>
          </div>
        </div>
      </div>

      <Rules compact={true} />

      {/* Team Scores Section */}
      <div className="team-scores-section">
        <h2>📊 Team Scores</h2>
        <div className="team-scores-grid">
          {teams
            .filter(team => team.groupId) // Filter out teams without valid groupId
            .map((team, index) => {
              // Ensure proper comparison between team.groupId and submission.groupId
              const teamSubmissions = submissions.filter(s => 
                String(s.groupId) === String(team.groupId) && s.status === 'correct'
              );
              const teamScore = teamSubmissions.reduce((total, submission) => {
                const item = getItemById(submission.itemId);
                return total + (item?.score || 0);
              }, 0);
              
              // Debug log for the first few teams
              if (index < 3) {
                console.log(`Team ${team.name} (ID: ${team.groupId}):`, {
                  totalSubmissions: submissions.filter(s => String(s.groupId) === String(team.groupId)).length,
                  correctSubmissions: teamSubmissions.length,
                  score: teamScore
                });
              }
              
              return {
                team,
                teamScore,
                correctSubmissions: teamSubmissions.length,
                totalSubmissions: submissions.filter(s => String(s.groupId) === String(team.groupId)).length,
                originalIndex: index // Add index for unique key fallback
              };
            })
            .sort((a, b) => parseInt(a.team.groupId) - parseInt(b.team.groupId)) // Sort by team ID (ascending)
            .map(({ team, teamScore, correctSubmissions, totalSubmissions, originalIndex }) => (
              <div key={`team-score-${team.groupId || `unknown-${originalIndex}`}`} className="team-score-card">
                <div className="team-picture-section">
                  {team.teamPicture ? (
                    <img 
                    onClick={() => handleViewTeamPage(team.groupId)}
                      src={`/api/uploads/${team.teamPicture}`} 
                      alt={`Team ${team.groupId}`}
                      className="team-picture"
                    />
                  ) : (
                    <div className="team-picture-placeholder" onClick={() => handleViewTeamPage(team.groupId)}>
                      
                      <div className="placeholder-icon">👥</div>
                      <div className="placeholder-text">No team photo</div>
                    </div>
                  )}
                </div>
                <div className="team-info-section">
                  <div className="team-score-header">
                    <div>Team #{team.groupId} <span className="team-name">{team.name}</span> <span className="team-score">{teamScore} pts</span></div>
                  </div>
                  <div className="team-score-details">
                    <div className="score-detail">
                      
                      <span className="value">Correct: {correctSubmissions}</span>
                    </div>
                    <div className="score-detail">                      
                      <span className="value">Submitted:{totalSubmissions}</span>
                    </div>
                  </div>
                  
                </div>
              </div>
            ))}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="filters">
        <button 
          className={filter === 'all' ? 'active' : ''} 
          onClick={() => setFilter('all')}
        >
          All ({submissions.length})
        </button>
        <button 
          className={filter === 'unknown' ? 'active' : ''} 
          onClick={() => setFilter('unknown')}
        >
          Pending ({submissions.filter(s => s.status === 'unknown').length})
        </button>
        <button 
          className={filter === 'correct' ? 'active' : ''} 
          onClick={() => setFilter('correct')}
        >
          Approved ({submissions.filter(s => s.status === 'correct').length})
        </button>
        <button 
          className={filter === 'wrong' ? 'active' : ''} 
          onClick={() => setFilter('wrong')}
        >
          Rejected ({submissions.filter(s => s.status === 'wrong').length})
        </button>
      </div>

      <div className="submissions-grid">
        {filteredSubmissions.map(submission => {
          const item = getItemById(submission.itemId);
          return (
            <div key={`${submission.groupId}-${submission.itemId}`} className="submission-card">
              <div className="submission-header">
                <h3>#{submission.itemId} - {item?.description || `Item #${submission.itemId}`}</h3>
                <div className="submission-meta">
                  <div className="team-info">
                    <span className="group-name">Team: {submission.groupId}</span>
                    <button 
                      className="view-team-btn"
                      onClick={() => submission.groupId && handleViewTeamPage(submission.groupId)}
                      title="Open team page in new tab"
                    >
                      👥 View Team
                    </button>
                  </div>
                  <span className="points">Points: {item?.score || 0}</span>
                </div>
              </div>

              <div className="submission-image" onClick={() => setSelectedImage(submission)}>
                <img
                  src={imageService.getImageUrl(submission.imagePath)}
                  alt={`Submission for ${item?.description}`}
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-image.png';
                  }}
                />
                <div className="image-overlay">
                  <span>Click to view full size</span>
                </div>
              </div>

              <div className="submission-status">
                <span 
                  className="current-status"
                  style={{ backgroundColor: getStatusColor(submission.status) }}
                >
                  {submission.status.toUpperCase()}
                </span>
                <div className="status-timestamp">
                  Submitted: {new Date(submission.timestamp).toLocaleDateString()}
                  {submission.reviewedAt && (
                    <div>Reviewed: {new Date(submission.reviewedAt).toLocaleDateString()}</div>
                  )}
                </div>
              </div>

              <div className="status-controls">
                <button 
                  className="status-btn correct"
                  onClick={() => handleStatusUpdate(submission.id!, 'correct')}
                  disabled={submission.status === 'correct'}
                >
                  ✓ 
                </button>
                <button 
                  className="status-btn wrong"
                  onClick={() => handleStatusUpdate(submission.id!, 'wrong')}
                  disabled={submission.status === 'wrong'}
                >
                  ✗ 
                </button>
                <button 
                  className="status-btn unknown"
                  onClick={() => handleStatusUpdate(submission.id!, 'unknown')}
                  disabled={submission.status === 'unknown'}
                >
                  ? 
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredSubmissions.length === 0 && (
        <div className="no-submissions">
          <p>No submissions found for the selected filter.</p>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>#{selectedImage.itemId} - {getItemById(selectedImage.itemId)?.description}</h3>
              <button className="modal-close-button" onClick={() => setSelectedImage(null)}>×</button>
            </div>
            
            <div className="image-modal-body">
              <img
                src={imageService.getImageUrl(selectedImage.imagePath)}
                alt={`Full size submission`}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder-image.png';
                }}
              />
            </div>

            <div className="image-modal-footer">
              <div className="modal-status-controls">
                <button 
                  className="status-btn correct"
                  onClick={() => {
                    handleStatusUpdate(selectedImage.id!, 'correct');
                    setSelectedImage(null);
                  }}
                >
                  ✓ Mark as Correct
                </button>
                <button 
                  className="status-btn wrong"
                  onClick={() => {
                    handleStatusUpdate(selectedImage.id!, 'wrong');
                    setSelectedImage(null);
                  }}
                >
                  ✗ Mark as Wrong
                </button>
              </div>
              <div className="submission-details">
                <p>Team: {selectedImage.groupId}</p>
                <p>Points: {getItemById(selectedImage.itemId)?.score}</p>
                <p>Submitted: {new Date(selectedImage.timestamp).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HuntmasterDashboard;
